/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface TransferCenterProps {
  id: string;
  name: string;
  description: string | null;
  communicationCenterId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class TransferCenterRecord extends Record<TransferCenterProps>({
  id: null as any,
  name: null as any,
  description: null as any,
  communicationCenterId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    description: string | null;
    communicationCenterId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): TransferCenterRecord {
    if (json == null) {
      throw new Error('TransferCenterRecord.fromJS: parameter cannot be null');
    }
    return new TransferCenterRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'TransferCenterJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'TransferCenterJson.name'),
      description: json.description,
      communicationCenterId: getFromOrThrow(
        json,
        (j) =>
          propValueOrDefault(j, 'communicationCenterId', '00000000-0000-0000-0000-000000000000'),
        'TransferCenterJson.communicationCenterId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'TransferCenterJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    description: string | null;
    communicationCenterId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [TransferCenterRecord, Error[]] {
    if (json == null) {
      throw new Error('TransferCenterRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new TransferCenterRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'TransferCenterJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'TransferCenterJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      description: tryOrDefault(
        () => json.description,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      communicationCenterId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(
                j,
                'communicationCenterId',
                '00000000-0000-0000-0000-000000000000',
              ),
            'TransferCenterJson.communicationCenterId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'TransferCenterJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: TransferCenterProps) {
    super(props);
    if (props == null) {
      throw new Error('TransferCenterRecord constructor parameter cannot be null');
    }
  }
}

export abstract class TransferCenterViewModelBase implements TransferCenterProps {
  protected readonly _props: TransferCenterProps;
  private _json?: TransferCenterProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get description(): string | null {
    return this._props.description;
  }
  get communicationCenterId(): string {
    return this._props.communicationCenterId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: TransferCenterProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        description: this._props.description,
        communicationCenterId: this._props.communicationCenterId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllTransferCenterEntities = (state: AppState) =>
  state.entityStores.transferCenter.entities;

export const selectTransferCenterById = (id: string) => (state: AppState) =>
  state.entityStores.transferCenter.entities.get(id);

export const selectTransferCenterMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.transferCenter.meta.get(id);

export const selectTransferCenterAndMetadataById = (id: string) =>
  createSelector(
    selectTransferCenterById(id),
    selectTransferCenterMetadataById(id),
    (transferCenter, meta) => ({ transferCenter, meta }),
  );

export function selectTransferCenterEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllTransferCenterEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, TransferCenterRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectTransferCenterEntitiesByName(name: string) {
  return createSelector(selectAllTransferCenterEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectTransferCenterEntitiesByDescription(description: string) {
  return createSelector(selectAllTransferCenterEntities, (entities) =>
    entities.filter((e) => e.description === description).valueSeq(),
  );
}

export function selectTransferCenterEntitiesByCommunicationCenterId(communicationCenterId: string) {
  return createSelector(selectAllTransferCenterEntities, (entities) =>
    entities.filter((e) => e.communicationCenterId === communicationCenterId).valueSeq(),
  );
}

export function selectTransferCenterEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllTransferCenterEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
